import type { ComponentType } from 'react';
import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Wallet, FileText, Package, FileCheck2, ShoppingCart,
  TrendingDown, Clock, ArrowRightLeft, Users, Truck,
  GripVertical, RotateCcw, CheckCheck, Settings2, Plus, X as XIcon, Headphones,
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import { useDialog } from '../components/DialogProvider';
import { WhatsAppIcon } from '../components/WhatsAppIcon';
import { Modal } from '../components/Modal';
import { telasNavegaveis, type TelaNavegavel } from '../lib/telas-navegaveis';

// Tipo local mais permissivo que TelaNavegavel — aceita WhatsAppIcon (ComponentType genérico)
// além dos ícones do Lucide (typeof Users = ForwardRefExoticComponent).
type IconComp = ComponentType<{ size?: number; strokeWidth?: number }>;
type Atalho = { to: string; label: string; icon: IconComp; grupo: string };

const atalhosAdmin: Atalho[] = [
  { to: '/atendimento', label: 'Atendimento', icon: WhatsAppIcon, grupo: 'Atendimento' },
  { to: '/caixa', label: 'Caixa', icon: ShoppingCart, grupo: 'Lancamentos' },
  { to: '/orcamentos', label: 'Orcamentos', icon: FileText, grupo: 'Lancamentos' },
  { to: '/produtos', label: 'Produtos', icon: Package, grupo: 'Arquivos' },
  { to: '/notas-fiscais', label: 'Notas Fiscais', icon: FileCheck2, grupo: 'Lancamentos' },
  { to: '/cotacoes', label: 'Cotacoes', icon: ShoppingCart, grupo: 'Lancamentos' },
  { to: '/contas-receber', label: 'Contas a Receber', icon: Wallet, grupo: 'Lancamentos' },
  { to: '/contas-pagar', label: 'Contas a Pagar', icon: TrendingDown, grupo: 'Lancamentos' },
  { to: '/notas-entrada', label: 'Notas de Entrada', icon: ArrowRightLeft, grupo: 'Lancamentos' },
  { to: '/trocas', label: 'Trocas e Devolucoes', icon: ArrowRightLeft, grupo: 'Lancamentos' },
  { to: '/cadastros/clientes', label: 'Clientes', icon: Users, grupo: 'Arquivos' },
  { to: '/cadastros/fornecedores', label: 'Fornecedores', icon: Truck, grupo: 'Arquivos' },
];

const atalhosVendedora: Atalho[] = [
  { to: '/atendimento', label: 'Atendimento', icon: WhatsAppIcon, grupo: 'Atendimento' },
  { to: '/caixa', label: 'Caixa', icon: ShoppingCart, grupo: 'Lancamentos' },
  { to: '/orcamentos', label: 'Orcamentos', icon: FileText, grupo: 'Lancamentos' },
  { to: '/produtos', label: 'Produtos', icon: Package, grupo: 'Arquivos' },
  { to: '/ponto/registrar', label: 'Bater Ponto', icon: Clock, grupo: 'Equipe' },
];

// Leitor de Voz — ferramenta PESSOAL do RUAN. Fica de fora do telasNavegaveis (que alimenta o
// menu) de proposito: so aparece na tela inicial de atalhos, e so pra ele (gate por login).
const LEITOR_ATALHO: Atalho = { to: '/ler', label: 'Leitor', icon: Headphones, grupo: 'Ferramentas' };

function chaveLocalStorage(userId: number | string): string {
  return `fdc_atalhos_ordem_${userId}`;
}

function carregarOrdem(chave: string): string[] | null {
  try {
    const raw = localStorage.getItem(chave);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((x) => typeof x === 'string')) {
      return parsed as string[];
    }
    return null;
  } catch {
    return null;
  }
}

function salvarOrdem(chave: string, ordem: string[]): void {
  try {
    localStorage.setItem(chave, JSON.stringify(ordem));
  } catch {
    // localStorage indisponível ou cheio — silencioso
  }
}

/** Constrói a lista de Atalho[] a partir de uma lista de rotas salvas,
 * resolvendo label/icon pelo catálogo (inclui itens do padrão não presentes no catálogo).
 * Lista salva é AUTORITÁRIA — não auto-appenda.
 */
function atalhosDeRotas(rotas: string[], catalogo: Atalho[], padrao: Atalho[]): Atalho[] {
  // mapa unificado: catálogo + padrão (padrão pode ter itens como WhatsApp não no catálogo)
  const mapa = new Map<string, Atalho>();
  for (const a of catalogo) mapa.set(a.to, a);
  for (const a of padrao) if (!mapa.has(a.to)) mapa.set(a.to, a);

  const resultado: Atalho[] = [];
  for (const to of rotas) {
    const atalho = mapa.get(to);
    if (atalho) resultado.push(atalho);
    // rotas que não existem mais no catálogo são silenciosamente descartadas
  }
  return resultado;
}

export function Dashboard() {
  const { usuario } = useAuth();
  const dialog = useDialog();

  const ehAdmin = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';
  const ehRuan = usuario?.login === 'RUAN';
  const podeConfigurar = ehAdmin;
  const baseSemLeitor = ehAdmin ? atalhosAdmin : atalhosVendedora;
  const listaBase = ehRuan ? [...baseSemLeitor, LEITOR_ATALHO] : baseSemLeitor;
  const userId = usuario?.id ?? 'anonimo';
  const chave = chaveLocalStorage(userId);

  // Catálogo completo de telas disponíveis (memoizado). O Leitor é do RUAN e NÃO entra no
  // telasNavegaveis (que alimenta o menu) — entra só aqui, pra ele poder re-adicionar pelo "+".
  const catalogo = useMemo(
    () => (ehRuan ? [...telasNavegaveis(podeConfigurar), LEITOR_ATALHO] : telasNavegaveis(podeConfigurar)),
    [podeConfigurar, ehRuan],
  );

  const [atalhos, setAtalhos] = useState<Atalho[]>(() => {
    const ordemSalva = carregarOrdem(chave);
    if (!ordemSalva) return listaBase;
    return atalhosDeRotas(ordemSalva, catalogo, listaBase);
  });

  const [modoReorganizar, setModoReorganizar] = useState(false);
  const [arrastando, setArrastando] = useState<string | null>(null);
  const [sobreAlvo, setSobreAlvo] = useState<string | null>(null);
  const arrastandoRef = useRef<string | null>(null);
  const [modalAdicionar, setModalAdicionar] = useState(false);

  // Quando o usuário muda (troca de login no mesmo navegador), recarrega a ordem
  useEffect(() => {
    const ordemSalva = carregarOrdem(chave);
    if (!ordemSalva) {
      setAtalhos(listaBase);
    } else {
      setAtalhos(atalhosDeRotas(ordemSalva, catalogo, listaBase));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave]);

  // O Leitor é novo e o RUAN pode já ter uma ordem salva (que não o inclui, então o card não
  // apareceria). Introduz o atalho UMA vez; depois disso respeita a decisão dele — se remover,
  // não volta (o flag impede re-adicionar).
  useEffect(() => {
    if (!ehRuan) return;
    const flag = `fdc_leitor_visto_${userId}`;
    if (localStorage.getItem(flag)) return;
    localStorage.setItem(flag, '1');
    setAtalhos((prev) => {
      if (prev.some((a) => a.to === '/ler')) return prev;
      const nova = [...prev, LEITOR_ATALHO];
      salvarOrdem(chave, nova.map((a) => a.to));
      return nova;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ehRuan, userId]);

  const reordenar = useCallback(
    (alvoTo: string) => {
      const origemTo = arrastandoRef.current;
      arrastandoRef.current = null;
      setArrastando(null);
      setSobreAlvo(null);
      if (!origemTo || origemTo === alvoTo) return;

      setAtalhos((prev) => {
        const ids = prev.map((a) => a.to);
        const from = ids.indexOf(origemTo);
        const to = ids.indexOf(alvoTo);
        if (from < 0 || to < 0) return prev;
        const nova = [...prev];
        const [movido] = nova.splice(from, 1);
        nova.splice(to, 0, movido!);
        salvarOrdem(chave, nova.map((a) => a.to));
        return nova;
      });
    },
    [chave],
  );

  function removerAtalho(to: string) {
    setAtalhos((prev) => {
      const nova = prev.filter((a) => a.to !== to);
      salvarOrdem(chave, nova.map((a) => a.to));
      return nova;
    });
  }

  function adicionarAtalho(atalho: Atalho) {
    setAtalhos((prev) => {
      if (prev.some((a) => a.to === atalho.to)) return prev;
      const nova = [...prev, atalho];
      salvarOrdem(chave, nova.map((a) => a.to));
      return nova;
    });
  }

  function restaurarPadrao() {
    try {
      localStorage.removeItem(chave);
    } catch {
      // silencioso
    }
    setAtalhos(listaBase);
    dialog.toast('Ordem padrão restaurada.');
  }

  function concluirReorganizar() {
    setModoReorganizar(false);
    setArrastando(null);
    setSobreAlvo(null);
  }

  // Telas disponíveis para adicionar = catálogo menos as que já estão nos atalhos
  const rotasAtuais = new Set(atalhos.map((a) => a.to));
  const telasDisponiveis = catalogo.filter((t) => !rotasAtuais.has(t.to));

  // Agrupa telas disponíveis por grupo
  const gruposDisponiveis = useMemo(() => {
    const grupos = new Map<string, TelaNavegavel[]>();
    for (const t of telasDisponiveis) {
      if (!grupos.has(t.grupo)) grupos.set(t.grupo, []);
      grupos.get(t.grupo)!.push(t);
    }
    return grupos;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atalhos, catalogo]);

  return (
    <div>
      <div className="pagina-header">
        <h2 className="pagina-titulo">Bem-vindo, {usuario?.nome}</h2>

        <div className="pagina-acoes">
          {modoReorganizar ? (
            <>
              <button
                type="button"
                onClick={restaurarPadrao}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '5px 10px', borderRadius: 6, fontSize: 13,
                  border: '1px solid var(--border)',
                  background: 'transparent',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                }}
              >
                <RotateCcw size={13} />
                Restaurar padrão
              </button>
              <button
                type="button"
                onClick={concluirReorganizar}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '5px 12px', borderRadius: 6, fontSize: 13,
                  border: '1px solid var(--accent)',
                  background: 'var(--accent)',
                  color: 'var(--accent-fg)',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                <CheckCheck size={14} />
                Concluir
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setModoReorganizar(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '4px 10px', borderRadius: 6, fontSize: 12,
                border: '1px solid var(--border)',
                background: 'transparent',
                color: 'var(--text-muted)',
                cursor: 'pointer',
              }}
            >
              <Settings2 size={13} />
              Reorganizar atalhos
            </button>
          )}
        </div>
      </div>

      {modoReorganizar && (
        <div style={{
          fontSize: 13, color: 'var(--text-muted)',
          marginBottom: 10, marginTop: 4,
          padding: '6px 10px',
          borderRadius: 6,
          border: '1px solid var(--border)',
          background: 'var(--panel-bg)',
        }}>
          Arraste os cards para reorganizar, use × para remover e + para adicionar atalhos.
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 mt-4">
        {atalhos.map(({ to, label, icon: Icon }) => {
          const esteArrastando = arrastando === to;
          const esteAlvo = sobreAlvo === to && arrastando !== to;

          if (modoReorganizar) {
            return (
              <div
                key={to}
                draggable
                onDragStart={(e) => {
                  arrastandoRef.current = to;
                  setArrastando(to);
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', to);
                }}
                onDragEnd={() => {
                  arrastandoRef.current = null;
                  setArrastando(null);
                  setSobreAlvo(null);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (sobreAlvo !== to) setSobreAlvo(to);
                }}
                onDragLeave={() => {
                  setSobreAlvo((prev) => (prev === to ? null : prev));
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  reordenar(to);
                }}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', gap: 8, padding: '5px 5px 12px 5px',
                  borderRadius: 8,
                  border: esteAlvo
                    ? '2px dashed var(--primary)'
                    : '1px solid var(--border)',
                  backgroundColor: esteArrastando
                    ? 'var(--primary-soft)'
                    : esteAlvo
                    ? 'var(--primary-soft)'
                    : 'var(--panel-bg)',
                  color: 'var(--page-fg)',
                  opacity: esteArrastando ? 0.5 : 1,
                  cursor: 'grab',
                  userSelect: 'none',
                  position: 'relative',
                  transition: 'border-color 0.1s, background-color 0.1s',
                }}
              >
                {/* Botão × remover */}
                <button
                  type="button"
                  onClick={() => removerAtalho(to)}
                  title="Remover atalho"
                  style={{
                    position: 'absolute', top: 4, left: 4,
                    width: 20, height: 20,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: '50%',
                    border: '1px solid var(--border)',
                    background: 'var(--panel-bg)',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    padding: 0,
                    lineHeight: 1,
                    fontSize: 13,
                  }}
                >
                  <XIcon size={11} strokeWidth={2.5} />
                </button>
                {/* Grip */}
                <div style={{
                  position: 'absolute', top: 4, right: 4,
                  color: 'var(--text-muted)',
                  display: 'inline-flex',
                }}>
                  <GripVertical size={14} />
                </div>
                <Icon size={36} strokeWidth={1.5} />
                <span style={{ fontSize: 12, fontWeight: 500, textAlign: 'center' }}>{label}</span>
              </div>
            );
          }

          // Fora do modo reorganizar: Link normal
          return (
            <Link
              key={to}
              to={to}
              className="flex flex-col items-center justify-center gap-2 p-5 rounded-lg border transition hover:shadow-md hover:-translate-y-0.5"
              style={{
                backgroundColor: 'var(--panel-bg)',
                borderColor: 'var(--border)',
                color: 'var(--page-fg)',
              }}
            >
              <Icon size={40} strokeWidth={1.5} />
              <span className="text-sm font-medium text-center">{label}</span>
            </Link>
          );
        })}

        {/* Card "+ Adicionar atalho" — só no modo reorganizar */}
        {modoReorganizar && (
          <button
            type="button"
            onClick={() => setModalAdicionar(true)}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: 8, padding: '5px 5px 12px 5px',
              borderRadius: 8,
              border: '2px dashed var(--primary)',
              background: 'transparent',
              color: 'var(--primary)',
              cursor: 'pointer',
              minHeight: 90,
              transition: 'background-color 0.1s',
            }}
          >
            <Plus size={28} strokeWidth={1.5} />
            <span style={{ fontSize: 12, fontWeight: 500 }}>Adicionar atalho</span>
          </button>
        )}
      </div>

      {/* Modal de adicionar atalho */}
      <Modal
        aberto={modalAdicionar}
        onFechar={() => setModalAdicionar(false)}
        titulo="Adicionar atalho"
        largura={480}
      >
        {telasDisponiveis.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 14, textAlign: 'center', padding: '24px 0' }}>
            Todos os atalhos disponíveis já estão na tela inicial.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {Array.from(gruposDisponiveis.entries()).map(([grupo, telas]) => (
              <div key={grupo}>
                <div style={{
                  fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
                  color: 'var(--text-muted)',
                  padding: '10px 4px 4px 4px',
                  borderBottom: '1px solid var(--border)',
                  marginBottom: 2,
                }}>
                  {grupo}
                </div>
                {telas.map((tela) => {
                  const Icon = tela.icon;
                  return (
                    <button
                      key={tela.to}
                      type="button"
                      onClick={() => adicionarAtalho(tela)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        width: '100%', textAlign: 'left',
                        padding: '8px 6px',
                        borderRadius: 6,
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--page-fg)',
                        cursor: 'pointer',
                        fontSize: 14,
                        transition: 'background-color 0.1s',
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--primary-soft)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                      }}
                    >
                      <Icon size={18} strokeWidth={1.5} />
                      <span>{tela.label}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}
